import TextButtonM from "@common/components/button/TextButtonM"
import StatusBadgeM from "@common/components/badge/StatusBadgeM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React from "react"
import { ScrollView, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { MaDispoWeekGrid } from "./MaDispoEditor"
import {
    dayTimeLabel,
    keptSeatLine,
    rattrapageLine,
    stageCards,
    trialCard,
    studentRowsBySeverity,
    untouchedLiveLine,
} from "./releaseSlotsModel"

/**
 * The steps of « Libérer des créneaux », rendered by FormStepper as `custom`
 * steps. They hold no state: the screen owns the draft and hands each step its
 * slice of it.
 */

const Hint = ({ children, style }) => (
    <TextM
        fontSize={13}
        color={theme.colors.light}
        wrap
        style={{ lineHeight: 19, ...style }}
    >
        {children}
    </TextM>
)

/** ① His week, in one grid: the créneaux with a live he can no longer give, and the free hours he gives. */
export const ReleaseSlotsWeekStep = ({
    slots,
    grid,
    preselectedLabel,
    onAddSlot,
    onRemoveSlot,
    onToggleEngaged,
    onStopAll,
    stopAllLoading,
}) => (
    <ScrollView contentContainerStyle={{ paddingBottom: 20 }}>
        {!!preselectedLabel && (
            <TextM fontWeight="Bold" fontSize={14} wrap style={{ marginBottom: 6, lineHeight: 20 }}>
                {`Ton ${preselectedLabel} est libéré. D'autres créneaux changent ?`}
            </TextM>
        )}
        <Hint>
            {preselectedLabel
                ? "Touche un autre créneau avec un live pour le libérer aussi. Touche une heure libre pour la proposer ou la retirer : les familles verront cette semaine."
                : "Touche les créneaux avec un live que tu ne peux plus assurer. Touche une heure libre pour la proposer ou la retirer : les familles verront cette semaine."}
        </Hint>
        {/* The grid carries its own gutter, the step already has one. */}
        <View style={{ marginHorizontal: -16 }}>
            <MaDispoWeekGrid
                slots={slots}
                grid={grid}
                onAddSlot={onAddSlot}
                onRemoveSlot={onRemoveSlot}
                onPressEngaged={onToggleEngaged}
                onPressFreed={onToggleEngaged}
            />
        </View>
        <TextButtonM
            label="J'arrête tous mes créneaux"
            fontWeight="Bold"
            color={theme.colors.primary}
            loading={stopAllLoading}
            onPress={onStopAll}
            style={{ marginTop: 6, marginBottom: 14 }}
        />
        <Hint>
            Une absence ponctuelle n'est pas ce geste : fais une demande
            d'absence.
        </Hint>
    </ScrollView>
)

const Card = ({ children, style }) => (
    <View
        style={{
            backgroundColor: theme.colors.white,
            borderWidth: 1,
            borderColor: theme.colors.lightMore,
            borderRadius: 12,
            paddingVertical: 12,
            paddingHorizontal: 14,
            marginBottom: 10,
            ...style,
        }}
    >
        {children}
    </View>
)

const TONE_STAYS = { bg: theme.colors.successLight, text: theme.colors.success }
const TONE_CANCELLED = {
    bg: theme.colors.warningLight,
    text: theme.colors.warning,
}
const TONE_AT_RISK = TONE_CANCELLED
const TONE_LOST = { bg: theme.colors.errorLight, text: theme.colors.error }
const ONE_OFF_TONES = { lost: TONE_LOST, atRisk: TONE_AT_RISK, stays: TONE_STAYS }

const StudentCard = ({ name, tag, tone, alert, lines, suggestion, suggestedSlots = [], onOpenSlot, openLoading }) => (
    <Card>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
            <TextM fontWeight="Bold" fontSize={14} wrap style={{ flex: 1 }}>
                {name}
            </TextM>
            <StatusBadgeM
                label={tag}
                color={tone.text}
                backgroundColor={tone.bg}
                fontSize={11.5}
                style={{ marginLeft: 8, alignSelf: "flex-start" }}
            />
        </View>
        {!!alert && (
            <TextM
                fontWeight="Bold"
                fontSize={13}
                color={tone.text}
                wrap
                style={{ marginTop: 6, lineHeight: 18 }}
            >
                {alert}
            </TextM>
        )}
        {lines.map((line, i) => (
            <TextM
                key={i}
                fontSize={13}
                color={theme.colors.grey}
                wrap
                style={{ marginTop: 4, lineHeight: 18 }}
            >
                {line}
            </TextM>
        ))}
        {!!suggestion && (
            <View
                style={{
                    marginTop: 10,
                    paddingTop: 10,
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.lightMore,
                }}
            >
                <TextM fontSize={13} wrap style={{ lineHeight: 18 }}>
                    {suggestion}
                </TextM>
                <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 4 }}>
                    {suggestedSlots.map((slot) => (
                        <TextButtonM
                            key={slot}
                            label={`Ajouter le ${dayTimeLabel(slot)}`}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            loading={openLoading}
                            onPress={() => onOpenSlot?.(slot)}
                            style={{ fontSize: 13, marginTop: 6, marginRight: 16 }}
                        />
                    ))}
                </View>
            </View>
        )}
    </Card>
)

/**
 * ② What the rules decide for each student, one card each: his sessions, what
 * happens to them, and — when he is at risk or lost — the créneaux of his family
 * the tutor could open to keep him. Opening one asks the preview again; it is the
 * tutor's only lever on a student's outcome.
 */
export const ReleaseSlotsStudentsStep = ({ preview, onOpenSlot, previewLoading }) => {
    const students = preview?.students ?? []
    const rattrapagesOf = (studentId) =>
        (preview?.rattrapages ?? []).filter((r) => String(r.student_id) === String(studentId))
    const visitors = (preview?.rattrapages ?? []).filter(
        (r) => !students.some((s) => String(s.student_id) === String(r.student_id))
    )
    const stages = preview?.stages ?? []
    const trials = preview?.trials ?? []
    const keptSeats = preview?.kept_seats ?? []

    return (
        <ScrollView contentContainerStyle={{ paddingBottom: 20 }}>
            {!!preview?.refusal && (
                <View
                    style={{
                        flexDirection: "row",
                        backgroundColor: theme.colors.warningLight,
                        borderRadius: 12,
                        padding: 14,
                        marginBottom: 12,
                    }}
                >
                    <FontAwesome5
                        name="exclamation-circle"
                        size={16}
                        color={theme.colors.warning}
                        style={{ marginRight: 10, marginTop: 1 }}
                    />
                    <TextM fontSize={13.5} wrap style={{ flex: 1, lineHeight: 19 }}>
                        {preview.refusal}
                    </TextM>
                </View>
            )}
            {studentRowsBySeverity(students).map(({ student, row }, index, rows) => (
                <StudentCard
                    key={`s-${student.student_id}-${index}`}
                    name={student.student_name}
                    tag={row.tag}
                    tone={row.lost ? TONE_LOST : row.atRisk ? TONE_AT_RISK : TONE_STAYS}
                    alert={row.alert}
                    lines={[
                        ...row.lines,
                        // A student whose two blocks part ways has two cards: his rattrapages go on the first.
                        ...(rows.findIndex((other) => other.student.student_id === student.student_id) === index
                            ? rattrapagesOf(student.student_id).map((r) => `Rattrapage du ${rattrapageLine(r)}`)
                            : []),
                    ]}
                    suggestion={row.suggestion}
                    suggestedSlots={row.suggestedSlots}
                    onOpenSlot={onOpenSlot}
                    openLoading={previewLoading}
                />
            ))}
            {visitors.map((rattrapage) => (
                <StudentCard
                    key={`r-${rattrapage.student_id}-${rattrapage.date}`}
                    name={rattrapage.student_name}
                    tag="rattrapage annulé"
                    tone={TONE_CANCELLED}
                    lines={[rattrapageLine(rattrapage)]}
                />
            ))}
            {keptSeats.map((kept) => (
                <StudentCard
                    key={`k-${kept.student_id}-${kept.day_time}`}
                    name={kept.student_name}
                    tag="nouveau tuteur"
                    tone={TONE_LOST}
                    lines={[keptSeatLine(kept)]}
                />
            ))}
            {!students.length && !visitors.length && !stages.length && !trials.length && !keptSeats.length && (
                <Hint>Aucun élève n'est concerné.</Hint>
            )}
            {[...stages.flatMap(stageCards), ...trials.map(trialCard)].map((card) => (
                <StudentCard key={card.key} name={card.name} tag={card.tag} tone={ONE_OFF_TONES[card.tone]} lines={card.lines} />
            ))}
            {(preview?.untouched_lives ?? []).map((live) => (
                <Hint key={`u-${live.live_session_id}`} style={{ marginTop: 6 }}>
                    {untouchedLiveLine(live)}
                </Hint>
            ))}
        </ScrollView>
    )
}
